import { callApi, ThrowError } from '@/api/callApi';
import { API_ENDPOINT, API_URL_PRODUCT_SERVICE } from '@/api/constant';
import CategoryDiscount from '@/components/category_brands/CategoryDiscount';
import Breadcrumb from '@/components/common/Breadcrumb';
import ErrorMessage from '@/components/common/ErrorMessage';
import MediaInputField from '@/components/common/media/MediaInputField';
import MediaPreview from '@/components/common/media/MediaPreview';
import Modal from '@/components/common/models/Modal';
import Layout from '@/components/Layout/Layout';
import ProductDragDrop from '@/components/product/ProductDragDrop';
import Card from '@/components/UI/Card';
import FormField from '@/components/UI/FormField';
import BtnLoader from '@/components/UI/loaders/BtnLoader';
import UploadField from '@/components/UI/UploadField';
import { ICategory } from '@/interfaces/category.interface';
import { IMediaProp } from '@/interfaces/mediaResponse.interface';
import { RoutesLink } from '@/utility/RouteLinks';
import { errorTypes } from '@/utility/validation';
import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';

const forbiddenColors = ['#000000', '#ffffff'];
const breadcrumb = [
  { label: 'Categories', href: RoutesLink.categoryList },
  { label: 'Edit Category' },
];

export const EditCategoryPage = () => {
  const { id } = useParams<{ id: string }>();
  if (!id) {
    return;
  }
  const categoryId = id;
  const navigate = useNavigate();
  const [mediaFile, setMediaFile] = useState<IMediaProp | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isInfoLoading, setIsInfoLoading] = useState(false);
  const [showPopup, setShowPopup] = useState<boolean>(false);

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    reset,
    control,
    formState: { errors },
  } = useForm<ICategory>({
    defaultValues: {
      name: '',
      colorCode: '',
      description: '',
      status: 'active',
      file: null,
      activeDiscount: null,
    },
  });

  const onSubmit = async (data: ICategory) => {
    try {
      setIsLoading(true);

      let PATH = `${API_URL_PRODUCT_SERVICE}${API_ENDPOINT.categories}`;
      if (categoryId) {
        PATH = `${PATH}/${categoryId}`;
      }
      await callApi(PATH, {
        method: 'PATCH',
        multipart: true,
        data,
      });
      navigate(RoutesLink.categoryList);
      setIsLoading(false);
    } catch (error) {
      setIsLoading(false);
      ThrowError(error);
    }
  };

  const onSaveInfo = async (data: ICategory) => {
    try {
      setIsInfoLoading(true);

      const { activeDiscount, ...categoryData } = data;

      const PATH = `${API_URL_PRODUCT_SERVICE}${API_ENDPOINT.categories}/${categoryId}`;

      await callApi(PATH, {
        method: 'PATCH',
        multipart: true,
        data: categoryData,
      });
      navigate(RoutesLink.categoryList);
      setIsInfoLoading(false);
    } catch (error) {
      setIsInfoLoading(false);
      ThrowError(error);
    }
  };

  const handleDeleteMedia = async (id?: string) => {
    if (id) {
      const PATH = `${API_URL_PRODUCT_SERVICE}${API_ENDPOINT.media}/${id}`;
      const res = await callApi(PATH, { method: 'DELETE' });
      if (res) {
        setMediaFile(null);
      }
    } else {
      setMediaFile(null);
    }
  };

  const fetchCategoryInfo = async () => {
    try {
      const PATH = `${API_URL_PRODUCT_SERVICE}${API_ENDPOINT.categories}/${categoryId}`;
      const res = await callApi(PATH, { method: 'GET' });
      reset({
        name: res.name,
        colorCode: res.colorCode,
        description: res.description,
        status: res.status,
        file: null,
        activeDiscount: res.activeDiscount,
      });

      if (res.media && res.media.length > 0) {
        setMediaFile({
          id: res.media[0].id,
          url: categoryId
            ? res.media[0].url
            : `${API_URL_PRODUCT_SERVICE}/${res.media[0].url}`,
          type: res.media[0].type,
          altText: res.media[0].altText || 'Category Image',
        });
      }
    } catch (err) {
      ThrowError(err);
    }
  };

  useEffect(() => {
    categoryId && fetchCategoryInfo();
  }, [categoryId, reset]);

  return (
    <Layout>
      <Breadcrumb items={breadcrumb} />
      <form className="space-y-5" onSubmit={handleSubmit(onSubmit)}>
        <Card>
          <div className="space-y-5">
            {/* About Category */}
            <h2 className="text-lg font-semibold mb-4">About Category</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <FormField
                  label="Name"
                  type="text"
                  placeholder="Enter category name"
                  {...register('name')}
                />
              </div>
              <div>
                <FormField
                  label="Color Code"
                  type="text"
                  placeholder="eg. #f5045d"
                  {...register('colorCode', {
                    ...errorTypes.required('Color code'),
                    ...errorTypes.colorCode,
                    validate: {
                      forbidden: (value) =>
                        !forbiddenColors.includes(value.trim().toLowerCase()) ||
                        'This color is not allowed',
                    },
                  })}
                />
                <ErrorMessage message={errors.colorCode?.message} />
              </div>
            </div>

            <div>
              <FormField
                type="textarea"
                label="Description"
                placeholder="Start describing the category..."
                rows={4}
                {...register('description', {
                  ...errorTypes.required('description'),
                })}
              />
              <ErrorMessage message={errors.description?.message} />
            </div>

            <div>
              <h4 className="block text-sm font-medium text-gray-700 mb-2">
                Upload Category Image
              </h4>

              {categoryId ? (
                <UploadField
                  referenceType="category"
                  referenceId={categoryId}
                  multiple={false}
                  initialMedia={mediaFile ? [mediaFile] : []}
                />
              ) : (
                <Controller
                  name="file"
                  control={control}
                  render={({ field }) => (
                    <MediaInputField
                      helperText="Upload category image"
                      multiple={false}
                      onFilesChange={(files) => {
                        if (files && files.length > 0) {
                          const file = files[0];
                          field.onChange(file);
                          setMediaFile({
                            url: URL.createObjectURL(file),
                            type: file.type.startsWith('video/')
                              ? 'video'
                              : 'image',
                            altText: file.name,
                          });
                        }
                      }}
                    />
                  )}
                />
              )}
              {mediaFile && !categoryId && (
                <div className="mt-4">
                  <MediaPreview
                    mediaUrls={[mediaFile]}
                    handleDeleteMedia={handleDeleteMedia}
                  />
                </div>
              )}
            </div>
          </div>
        </Card>
        <div className="flex justify-end gap-3 mb-5">
          <button
            onClick={() => navigate(-1)}
            type="button"
            disabled={isInfoLoading}
            className="btn-secondary"
          >
            Cancel
          </button>
          <button
            disabled={isInfoLoading}
            type="button"
            className="btn-primary"
            onClick={handleSubmit(onSaveInfo)}
          >
            {isInfoLoading && <BtnLoader />} Save Info
          </button>
        </div>
        <CategoryDiscount
          register={register}
          watch={watch}
          setValue={setValue}
          control={control}
          error={errors}
        />

        {/* Action Buttons */}
        <div className="flex justify-end gap-3 mb-5">
          <button
            onClick={() => navigate(-1)}
            type="button"
            disabled={isLoading}
            className="btn-secondary"
          >
            Cancel
          </button>
          <button
            disabled={isLoading}
            type="button"
            onClick={() => setShowPopup(true)}
            className="btn-primary"
          >
            {isLoading && <BtnLoader />} Update Discount
          </button>
        </div>
        <Modal
          show={showPopup}
          onClose={() => setShowPopup(false)}
          title="Save Changes"
          size="w-[500px] top-1/2! -translate-y-1/2"
          height="max-h-70"
        >
          <div className="w-full">
            <p className="mb-5 text-sm text-gray-600">
              Are you sure you want to continue?
            </p>
            <div className="mb-6 flex gap-3 rounded-lg border border-amber-200 bg-amber-50 p-4">
              <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-amber-100">
                <svg
                  className="h-4 w-4 text-amber-600"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M12 9v4m0 4h.01M10.29 3.86l-8.18 14a2 2 0 001.73 3h16.32a2 2 0 001.73-3l-8.18-14a2 2 0 00-3.42 0z"
                  />
                </svg>
              </div>

              <div className="flex-1">
                <p className="text-sm font-medium text-amber-800">
                  Updating discount on category will apply discount on all
                </p>

                <p className="mt-1 text-sm leading-5 text-amber-900">
                  products under this category and will remove any existing
                  product level discounts
                </p>
              </div>
            </div>

            {/* Confirmation */}

            {/* Actions */}
            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={() => setShowPopup(false)}
                className="btn btn-secondary min-w-[110px]"
              >
                Cancel
              </button>

              <button
                disabled={isLoading || isInfoLoading}
                type="submit"
                className="btn btn-primary min-w-[110px]"
              >
                {isLoading && <BtnLoader />}
                {!isLoading && 'Update'}
              </button>
            </div>
          </div>
        </Modal>
      </form>
      <ProductDragDrop categoryId={id} />
    </Layout>
  );
};
